import React from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { getOrderTotals } from "./constants";
import { useThemeColor } from "../hooks/useThemeColor";

export default function OrderDetails({
  storeDetails,
  order,
  orderLoading,
  orderError,
  onContinue,
}) {
  const { themeColor } = useThemeColor();
  const { store } = useSelector((state) => state.auth);
  const displayOrder = order ?? {};
  const displayStore = storeDetails ?? (store?.name ? store : null);
  const { subtotal, tax, total } = getOrderTotals(displayOrder);
  const primaryColor = themeColor || "#162A5C";

  if (orderLoading && !order) {
    return (
      <Container maxWidth="md" className="pay-at-table-step" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <CircularProgress sx={{ color: primaryColor }} />
        </Box>
      </Container>
    );
  }

  if (orderError && !order) {
    return (
      <Container maxWidth="md" className="pay-at-table-step" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }} className="pay-at-table-card">
          <Typography color="error">{orderError}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please scan the table QR code again or open the link from your table.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" className="pay-at-table-step" sx={{ py: 1 }}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 2,
          overflow: "hidden",
        }}
        className="pay-at-table-card"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 4 },
            alignItems: { xs: "flex-start", md: "stretch" },
          }}
        >
          <Box sx={{ flex: 1.1, textAlign: "left" }}>
            {displayStore?.name && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="overline"
                  sx={{ letterSpacing: 1.2 }}
                  color="text.secondary"
                >
                  Paying at
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="text.primary"
                  sx={{ mt: 0.5 }}
                >
                  {displayStore.name}
                </Typography>
                {(displayStore.address_line_1 ||
                  displayStore.address_line_2 ||
                  displayStore.city) && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25 }}
                  >
                    {[displayStore.address_line_1, displayStore.address_line_2, displayStore.city]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography>
                )}
              </Box>
            )}
            <Typography
              variant="h4"
              fontWeight={800}
              color="text.primary"
              sx={{ mb: 0.5 }}
            >
              Table {displayOrder.tableId}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Please confirm your order before proceeding to secure payment.
            </Typography>
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 1.5,
                background:
                  "linear-gradient(135deg, rgba(22,42,92,0.06), rgba(22,42,92,0.02))",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Order reference
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {displayOrder?.orderId ? `#${displayOrder.orderId}` : 'N/A'}
              </Typography>
            </Box>
          </Box>

        <Box sx={{ flex: 1, textAlign: "left" }}>
          {(displayOrder.items || []).map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.75,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body1">
                {item.qty}x {item.name}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {displayOrder.currency}
                {(item.qty * item.price).toFixed(2)}
              </Typography>
            </Box>
          ))}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, py: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Subtotal:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayOrder.currency}
              {subtotal.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Tax:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayOrder.currency}
              {tax.toFixed(2)}
            </Typography>
          </Box>
        </Box>
        </Box>

        <Box
          sx={{
            bgcolor: "grey.100",
            borderRadius: 1,
            p: 2,
            border: "1px solid",
            borderColor: "grey.300",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Total:
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {displayOrder.currency}
              {total.toFixed(2)}
            </Typography>
          </Box>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={onContinue}
            sx={{
              bgcolor: primaryColor,
              color: "#fff",
              py: 1.5,
              "&:hover": { bgcolor: primaryColor, filter: "brightness(1.1)" },
            }}
          >
            Continue to Pay
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
